import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { ShoppingBag, X } from 'lucide-react'
import { useCart } from '@/context/CartContext'
import { CartItem } from './CartItem'
import { CartSummary } from './CartSummary'
import { Button } from '@/components/ui/Button'
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll'
import { ROUTES } from '@/constants'

export function CartDrawer() {
  const cart = useCart()
  useLockBodyScroll(cart.isOpen)
  return (
    <AnimatePresence>
      {cart.isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={cart.closeDrawer}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-modal"
            aria-label="Shopping cart"
          >
            <div className="flex items-center justify-between border-b border-charcoal-100 px-4 py-3 sm:px-5 sm:py-4">
              <h3 className="text-lg font-bold text-charcoal">
                Cart {cart.cartCount > 0 && <span className="text-coral">({cart.cartCount})</span>}
              </h3>
              <button
                type="button"
                aria-label="Close cart"
                onClick={cart.closeDrawer}
                className="grid h-11 w-11 place-items-center rounded-full hover:bg-sand-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-5">
              {cart.items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="grid h-20 w-20 place-items-center rounded-full bg-coral-50">
                    <ShoppingBag size={32} className="text-coral" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold text-charcoal">Your cart is empty</h4>
                  <p className="mt-1 text-sm text-charcoal-600">Discover something your little one will love.</p>
                  <Button as={Link} to={ROUTES.shop} onClick={cart.closeDrawer} className="mt-6">
                    Shop Now
                  </Button>
                </div>
              ) : (
                cart.items.map((item) => <CartItem key={item.id} item={item} compact />)
              )}
            </div>

            {cart.items.length > 0 && (
              <div className="pb-safe border-t border-charcoal-100 p-4 sm:p-5">
                <CartSummary onNavigate={cart.closeDrawer} />
                <Link
                  to={ROUTES.cart}
                  onClick={cart.closeDrawer}
                  className="mt-3 block text-center text-sm font-medium text-charcoal-600 hover:text-coral"
                >
                  View full cart
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
